import { useState, useEffect } from 'react';
import { AlertCircle, Clock, X } from 'lucide-react';

/**
 * Rate Limit Notification
 * Displays a toast notification when API rate limits are exceeded
 */
const RateLimitNotification = () => {
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState('');
  const [_retryAfter, setRetryAfter] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    const handleRateLimit = (event) => {
      const { message, retryAfter, _endpoint } = event.detail;

      setMessage(message || 'Too many requests. Please slow down.');
      setRetryAfter(retryAfter);
      setShow(true);

      // Calculate countdown if retryAfter is a timestamp
      if (retryAfter && typeof retryAfter === 'number') {
        const resetTime = retryAfter * 1000; // Convert to milliseconds
        const now = Date.now();
        const secondsRemaining = Math.ceil((resetTime - now) / 1000);
        setCountdown(secondsRemaining > 0 ? secondsRemaining : null);
      }

      // Auto-hide after 10 seconds
      setTimeout(() => {
        setShow(false);
      }, 10000);
    };

    window.addEventListener('rate-limit-exceeded', handleRateLimit);
    return () => {
      window.removeEventListener('rate-limit-exceeded', handleRateLimit);
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (countdown === null || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const formatTime = (seconds) => {
    if (!seconds) return '';

    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (remainingSeconds === 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }

    return `${minutes}m ${remainingSeconds}s`;
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[100] max-w-md animate-slide-in-right"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 dark:border-yellow-400 rounded-lg shadow-lg dark:shadow-yellow-900/20 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
              Rate Limit Exceeded
            </h3>
            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
              {message}
            </p>

            {countdown !== null && (
              <div className="flex items-center gap-2 text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 px-3 py-2 rounded-md">
                <Clock size={14} aria-hidden="true" />
                <span>
                  Try again in <strong>{formatTime(countdown)}</strong>
                </span>
              </div>
            )}

            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2">
              Please wait before making more requests.
            </p>
          </div>

          <button
            onClick={() => setShow(false)}
            className="flex-shrink-0 p-1 rounded-md hover:bg-yellow-100 dark:hover:bg-yellow-900/40 transition-colors"
            aria-label="Close notification"
          >
            <X size={18} className="text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RateLimitNotification;
